import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { mockTradingRulesConfig } from '../mock/tradingRulesMetricsMock';
import { resolveTradingRulesMockScenario } from '../utils/tradingRulesMockScenario';
import useAPI from '../hooks/useApi';
import { TradingContext } from './TradingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TradingRulesConfigContext = createContext(null);

/**
 * TradingRulesConfigProvider - Centralized state management for trading rules configuration
 *
 * Provides trading rules config to all components, eliminating the need for refetch calls.
 * State updates automatically after save, so all components using this context get updated config immediately.
 */
export const TradingRulesConfigProvider = ({ children }) => {
  const { selectedAccount } = useContext(TradingContext);
  const accountId = selectedAccount?.AccountID || null;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { request } = useAPI();
  const requestRef = useRef(request);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  // Deduplicate: never fire overlapping GETs for the same account (effect storms).
  const fetchedAccountRef = useRef(null);
  const inFlightAccountRef = useRef(null);

  const fetchSettings = useCallback(async (force = false) => {
    if (!accountId) {
      fetchedAccountRef.current = null;
      inFlightAccountRef.current = null;
      setSettings(null);
      setLoading(false);
      return;
    }

    if (!force && (fetchedAccountRef.current === accountId || inFlightAccountRef.current === accountId)) {
      return;
    }

    inFlightAccountRef.current = accountId;
    setLoading(true);
    setError(null);

    try {
      const { useMock } = resolveTradingRulesMockScenario();

      if (useMock) {
        await new Promise(resolve => setTimeout(resolve, 300));
        if (inFlightAccountRef.current !== accountId) return;
        setSettings(mockTradingRulesConfig);
        fetchedAccountRef.current = accountId;
        console.log('[TradingRulesConfigContext] Using mock data');
      } else {
        const response = await requestRef.current(
          `${BACKEND_URL}/trading_rules_config?account_id=${accountId}`,
          {},
          'GET'
        );
        if (inFlightAccountRef.current !== accountId) return;
        setSettings(response);
        fetchedAccountRef.current = accountId;
      }

      setLoading(false);
    } catch (err) {
      if (inFlightAccountRef.current !== accountId) return;
      console.error('[TradingRulesConfigContext] Error fetching settings:', err);
      setError(err.message || 'Failed to load settings');
      setLoading(false);
    } finally {
      if (inFlightAccountRef.current === accountId) {
        inFlightAccountRef.current = null;
      }
    }
  }, [accountId]);

  // Fetch once per accountId — do not depend on fetchSettings identity.
  useEffect(() => {
    if (!accountId) {
      fetchedAccountRef.current = null;
      inFlightAccountRef.current = null;
      setSettings(null);
      setLoading(false);
      return undefined;
    }
    if (fetchedAccountRef.current === accountId || inFlightAccountRef.current === accountId) {
      return undefined;
    }
    void fetchSettings(false);
    return undefined;
  }, [accountId, fetchSettings]);

  /**
   * Save trading rules settings
   *
   * Updates state immediately after successful save, so all components get updated config
   * without needing to refetch.
   *
   * @param {Object} newSettings - Settings object matching trading_rules_config shape
   * @returns {Promise<boolean>} Success status
   */
  const saveSettings = useCallback(async (newSettings) => {
    if (!accountId) {
      const errorMsg = 'Cannot save settings: No account selected';
      console.error('[TradingRulesConfigContext]', errorMsg);
      setError(errorMsg);
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      const { useMock } = resolveTradingRulesMockScenario();

      if (useMock) {
        await new Promise(resolve => setTimeout(resolve, 500));

        const settingsToSave = {
          ...newSettings,
          account_id: accountId,
          updated_at: new Date().toISOString()
        };

        setSettings(settingsToSave);
        fetchedAccountRef.current = accountId;
        setSaving(false);

        console.log('[TradingRulesConfigContext] Settings saved (mock mode):', {
          accountID: accountId,
          enforcement: settingsToSave.enforcement_config
        });

        return true;
      } else {
        const response = await requestRef.current(
          `${BACKEND_URL}/trading_rules_config`,
          {},
          'POST',
          {
            ...newSettings,
            account_id: accountId
          }
        );

        if (response && response.error) {
          const error = new Error(response.error);
          error.error = response.error;
          throw error;
        }

        setSettings(response);
        fetchedAccountRef.current = accountId;
        setSaving(false);

        console.log('[TradingRulesConfigContext] Settings saved to API:', {
          accountID: accountId,
          enforcement: response.enforcement_config
        });

        return true;
      }
    } catch (err) {
      console.error('[TradingRulesConfigContext] Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
      setSaving(false);
      throw err;
    }
  }, [accountId]);

  const refetch = useCallback(() => fetchSettings(true), [fetchSettings]);

  const value = useMemo(() => ({
    settings,
    loading,
    error,
    saving,
    saveSettings,
    hasSettings: !!settings,
    refetch,
    applySettings: setSettings,
  }), [settings, loading, error, saving, saveSettings, refetch]);

  return (
    <TradingRulesConfigContext.Provider value={value}>
      {children}
    </TradingRulesConfigContext.Provider>
  );
};

/**
 * Hook to access trading rules config context
 *
 * @returns {Object} { settings, loading, error, saving, saveSettings, hasSettings, refetch }
 */
export const useTradingRulesConfig = () => {
  const context = useContext(TradingRulesConfigContext);
  if (!context) {
    throw new Error('useTradingRulesConfig must be used within TradingRulesConfigProvider');
  }
  return context;
};

export default TradingRulesConfigContext;
